import { useState } from 'react';
import axios from 'axios';
import { Flag, X, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_REASONS = [
  "Inappropriate content",
  "Harassment or bullying",
  "Spam or scam",
  "Impersonation",
  "Underage content",
  "Other",
];

export function ReportModal({ isOpen, onClose, targetType, targetId, targetName }) {
  const [reason, setReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    const finalReason = reason === 'Other' ? customReason.trim() : reason;
    if (!finalReason) return;
    
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/reports`, {
        target_type: targetType,
        target_id: targetId,
        reason: finalReason,
      }, { withCredentials: true });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setReason('');
    setCustomReason('');
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={handleClose}>
      <div className="bg-[#1A1D24] rounded-t-2xl sm:rounded-2xl w-full max-w-sm border border-white/10 p-5" onClick={e => e.stopPropagation()} data-testid="report-modal">
        {submitted ? (
          <div className="text-center py-4">
            <AlertTriangle className="w-10 h-10 text-[#F59E0B] mx-auto mb-3" />
            <h3 className="text-white font-bold mb-1">Report Submitted</h3>
            <p className="text-sm text-[#71717A] mb-4">Thanks for helping keep Animus safe. We'll review this shortly.</p>
            <button onClick={handleClose} className="px-6 py-2 bg-[#E11D48] text-white text-sm font-medium rounded-full" data-testid="report-done-btn">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-[#E11D48]" />
                <h3 className="text-white font-bold">Report {targetName || targetType}</h3>
              </div>
              <button onClick={handleClose} className="p-1 text-[#71717A] hover:text-white" data-testid="report-close-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-xs text-[#71717A] mb-3">Why are you reporting this?</p>

            <div className="space-y-1.5 mb-4">
              {REPORT_REASONS.map(r => (
                <button
                  key={r}
                  onClick={() => setReason(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    reason === r
                      ? 'bg-[#E11D48]/20 text-[#E11D48] border border-[#E11D48]/40'
                      : 'bg-white/5 text-[#A1A1AA] border border-white/5 hover:bg-white/10'
                  }`}
                  data-testid={`report-reason-${r.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {r}
                </button>
              ))}
            </div>

            {reason === 'Other' && (
              <textarea
                value={customReason}
                onChange={(e) => setCustomReason(e.target.value)}
                placeholder="Describe the issue..."
                className="w-full bg-[#0F1115] text-white text-sm rounded-lg p-3 border border-white/10 focus:border-[#E11D48]/50 focus:outline-none resize-none h-20 mb-4 placeholder:text-[#555]"
                maxLength={500}
                data-testid="report-custom-reason"
              />
            )}

            {error && <p className="text-xs text-[#E11D48] mb-3">{error}</p>}

            <button
              onClick={handleSubmit}
              disabled={!reason || (reason === 'Other' && !customReason.trim()) || submitting}
              className="w-full py-2.5 bg-[#E11D48] text-white text-sm font-medium rounded-full disabled:opacity-40 transition-opacity"
              data-testid="report-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function BlockButton({ userId, userName, onBlocked }) {
  const [blocking, setBlocking] = useState(false);

  const handleBlock = async () => {
    if (!window.confirm(`Block ${userName || 'this user'}? They won't be able to see your content.`)) return;
    setBlocking(true);
    try {
      await axios.post(`${API_URL}/api/blocks/${userId}`, {}, { withCredentials: true });
      if (onBlocked) onBlocked();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to block user');
    } finally {
      setBlocking(false);
    }
  };

  return (
    <button
      onClick={handleBlock}
      disabled={blocking}
      className="px-3 py-1.5 text-xs text-[#E11D48] bg-[#E11D48]/10 border border-[#E11D48]/20 rounded-full hover:bg-[#E11D48]/20 transition-colors disabled:opacity-50"
      data-testid="block-user-btn"
    >
      {blocking ? 'Blocking...' : 'Block User'}
    </button>
  );
}
